import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArticleOutlined, DescriptionOutlined } from '@mui/icons-material';

const Policies = () => {
  const server_url = process.env.REACT_APP_API_URI;
  const [policies, setPolicies] = useState({
    terms: { loading: true, error: '', data: null, list: [] },
    privacy: { loading: true, error: '', data: null, list: [] },
  });

  const policyCards = useMemo(
    () => [
      {
        key: 'terms',
        title: 'Terms & Conditions',
        description: 'Define the legal terms for using the platform',
        icon: ArticleOutlined,
        previewPath: '/terms',
        endpoint: 'terms',
      },
      {
        key: 'privacy',
        title: 'Privacy Policy',
        description: 'Explain how user data is collected and used',
        icon: DescriptionOutlined,
        previewPath: '/privacy',
        endpoint: 'privacy',
      },
    ],
    [],
  );

  useEffect(() => {
    const fetchPolicy = async (key, endpoint) => {
      try {
        const response = await axios.get(
          `${server_url}/admin/legal/${endpoint}`,
        );
        const list = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const sorted = [...list].sort((a, b) => {
          const aDate = new Date(
            a?.updatedAt || a?.effectiveAt || a?.createdAt || 0,
          ).getTime();
          const bDate = new Date(
            b?.updatedAt || b?.effectiveAt || b?.createdAt || 0,
          ).getTime();
          return bDate - aDate;
        });
        const current = sorted[0] || null;
        setPolicies((prev) => ({
          ...prev,
          [key]: { loading: false, error: '', data: current, list: sorted },
        }));
      } catch (error) {
        setPolicies((prev) => ({
          ...prev,
          [key]: {
            loading: false,
            error: 'Failed to load content.',
            data: null,
            list: [],
          },
        }));
      }
    };

    policyCards.forEach((policy) => {
      fetchPolicy(policy.key, policy.endpoint);
    });
  }, [policyCards, server_url]);

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatus = (policyState) => {
    if (policyState.loading) {
      return { label: 'Loading', classes: 'bg-gray-100 text-gray-600' };
    }
    if (policyState.error) {
      return { label: 'Error', classes: 'bg-red-100 text-red-600' };
    }
    if (!policyState.data) {
      return { label: 'Missing', classes: 'bg-amber-100 text-amber-700' };
    }
    return { label: 'Live', classes: 'bg-emerald-100 text-emerald-700' };
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Policy Editor
        </h1>
        <p className="text-gray-600">
          Manage Terms & Conditions and Privacy Policy content from one place
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {policyCards.map((policy) => {
          const policyState = policies[policy.key];
          const status = getStatus(policyState);
          const data = policyState.data;
          const updatedAt =
            data?.updatedAt || data?.effectiveAt || data?.createdAt;
          const Icon = policy.icon;
          const recentVersions = policyState.list.slice(0, 3);

          return (
            <div
              key={policy.key}
              className="bg-white rounded-2xl border border-gray-100 shadow-md p-6 flex flex-col gap-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                    <Icon className="text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800">
                      {policy.title}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {policy.description}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${status.classes}`}
                >
                  {status.label}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Versions
                  </p>
                  <p className="text-2xl font-semibold text-gray-800">
                    {policyState.loading ? '—' : policyState.list.length}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Current Version
                  </p>
                  <p className="text-2xl font-semibold text-gray-800">
                    {policyState.loading ? '—' : data?.version || '1.0'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 text-sm text-gray-500">
                <span className="font-medium text-gray-700">
                  {data?.title || policy.title}
                </span>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Updated {formatDate(updatedAt)}</span>
                  <span>ID: {data?._id || '—'}</span>
                </div>
              </div>

              {recentVersions.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">
                    Recent Versions
                  </p>
                  <div className="space-y-2">
                    {recentVersions.map((item) => (
                      <div
                        key={item._id}
                        className="flex items-center justify-between text-xs text-gray-600"
                      >
                        <span>{item.version || '1.0'}</span>
                        <span>
                          {formatDate(item.effectiveAt || item.updatedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {policyState.error && (
                <div className="text-sm text-red-600">{policyState.error}</div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/admin/legal/${policy.endpoint}`}
                  className="flex-1 text-center border border-gray-200 px-4 py-2 rounded-lg font-semibold text-gray-700 hover:border-gray-300 hover:text-gray-900 transition-all"
                >
                  Manage Versions
                </Link>
                <a
                  href={policy.previewPath}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 text-center border border-gray-200 px-4 py-2 rounded-lg font-semibold text-gray-700 hover:border-gray-300 hover:text-gray-900 transition-all"
                >
                  View Live
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Policies;
